if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmitHandler.bind(this);
      }

      initializeForm() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-product-form-startup], [type="submit"]');
        // A defined element can connect before the parser inserts its children.
        if (!this.form || !this.submitButton || !this.variantIdInput) return false;
        const startupStatus = this.querySelector('[data-product-form-startup-status]');
        // The no-JS element follows the button, including its complete label.
        if (this.submitButton.hasAttribute('data-product-form-startup') && !this.form.querySelector('[data-product-form-startup-end]')) return false;

        this.cart = document.querySelector('sugar-cart-drawer');
        this.submitButtonText = this.submitButton?.querySelector('[data-submit-text], .buy-button__text, span');
        this.submitButtonTextOriginalHtml = this.submitButtonText?.innerHTML || '';
        this.errorMessageWrapper = null;
        this.errorMessage = null;
        this.error = false;

        if (this.cart) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.skipCart = this.dataset.skipCart === 'true';

        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmit);
        this.form.setAttribute('data-product-form-ready', '');
        if (this.submitButton.hasAttribute('data-product-form-startup')) this.submitButton.type = 'submit';
        if (startupStatus) startupStatus.hidden = true;
        return true;
      }

      connectedCallback() {
        this.mountGeneration = (this.mountGeneration || 0) + 1;
        if (this.initializeForm()) return;
        this.startupObserver = new MutationObserver(() => {
          if (this.initializeForm()) {
            this.startupObserver.disconnect();
            this.startupObserver = null;
          }
        });
        this.startupObserver.observe(this, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this.mountGeneration = (this.mountGeneration || 0) + 1;
        this.startupObserver?.disconnect();
        this.startupObserver = null;
        this.form?.removeEventListener('submit', this.onSubmit);
        this.form?.removeAttribute('data-product-form-ready');
        if (this.submitButton?.classList.contains('loading')) {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
        if (this.submitButton?.hasAttribute('data-product-form-startup')) this.submitButton.type = 'button';
      }

      captureSubmission() {
        return {
          generation: this.mountGeneration, form: this.form, button: this.submitButton,
          spinner: this.querySelector('.loading__spinner'), skipCart: this.skipCart,
          upsells: Array.from(this.form.sugarUpsellComponents || [], (component) =>
            ({ component, items: [...(component.queuedItems || [])] })),
        };
      }

      isCurrentSubmission(submission) {
        return submission.generation === this.mountGeneration && submission.form === this.form;
      }

      /**
       * Get the cart element and ensure it's upgraded to custom element
       * @returns {Promise<HTMLElement|null>}
       */
      async getUpgradedCart() {
        const cartElement = document.querySelector('sugar-cart-drawer');
        const cartType = 'sugar-cart-drawer';

        if (!cartElement) return null;
        this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        // Check if the custom element is already defined and the element is upgraded
        if (customElements.get(cartType) && typeof cartElement.getSectionsToRender === 'function') {
          return cartElement;
        }

        // Wait for the custom element to be defined (timeout after 2 seconds)
        try {
          await Promise.race([
            customElements.whenDefined(cartType),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 2000))
          ]);

          // Force upgrade the element if needed
          customElements.upgrade(cartElement);

          return cartElement;
        } catch (e) {
          console.warn(`[ProductForm] Failed to get upgraded ${cartType}:`, e);
          return cartElement; // Return the element anyway, methods will be checked individually
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.form?.hasAttribute('data-product-form-ready')) return;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check if an offers block is controlling this form
        const offersBlock = this.closest('.shopify-section')?.querySelector('.offers');
        if (offersBlock) {
          const selectedOffer = offersBlock.querySelector('.offer.is-selected');
          if (selectedOffer) {
            const quantity = parseInt(selectedOffer.dataset.quantity, 10) || 1;
            const variantId = selectedOffer.dataset.variantId;
            const isLocked = selectedOffer.dataset.variantLocked === 'true';

            // Always use custom handler when offers block is present
            // This ensures variant selections within offers are respected
            return this.handleOffersSubmit(selectedOffer, quantity, variantId, isLocked);
          }
          const startupStatus = this.querySelector('[data-product-form-startup-status]');
          if (startupStatus) startupStatus.hidden = false;
          return;
        }

        this.handleErrorMessage();
        this.error = false;
        const submission = this.captureSubmission();
        const quickAddModal = this.closest('quick-add-modal');

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        let formData = new FormData(this.form);

        // Force quantity if configured
        const forceQuantity = parseInt(this.dataset.forceQuantity, 10);
        if (forceQuantity > 0) {
          formData.set('quantity', forceQuantity);
        }

        // Ensure selling_plan is included if subscription is selected
        const sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
        if (sellingPlanInput && !sellingPlanInput.disabled && sellingPlanInput.value) {
          formData.set('selling_plan', sellingPlanInput.value);
        }

        const queuedItems = this.getQueuedUpsellItems();
        if (queuedItems.length) {
          const bundledData = new FormData();
          for (const [key, value] of formData.entries()) {
            const property = key.match(/^properties\[(.*)\]$/);
            const itemKey = property ? `items[0][properties][${property[1]}]`
              : ['id', 'quantity', 'selling_plan'].includes(key) ? `items[0][${key}]` : key;
            bundledData.append(itemKey, value);
          }
          queuedItems.forEach((item, index) => {
            bundledData.append(`items[${index + 1}][id]`, item.id);
            bundledData.append(`items[${index + 1}][quantity]`, item.quantity);
          });
          formData = bundledData;
        }

        // Get cart element with proper custom element upgrade
        const cart = await this.getUpgradedCart();
        if (!this.isCurrentSubmission(submission)) return;
        this.cart = cart;


        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          const sections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        } else {
          console.warn('[ProductForm] Cart element missing getSectionsToRender method');
        }
        config.body = formData;


        try {
          const result = await window.SugarTheme.cart.request('add', formData, {
            source: 'product-form', context: submission.form, open: !submission.skipCart && !quickAddModal,
          });
          if (!this.isCurrentSubmission(submission)) return result;
          this.clearQueuedUpsells(submission);
          if (submission.skipCart) {
            window.location.href = '/checkout';
          } else if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              if (this.isCurrentSubmission(submission)) cart?.open?.();
            }, { once: true });
            quickAddModal.hide(true);
          } else if (!this.cart) {
            window.location.href = routes.cart_url;
          }
          return result;
        } catch (error) {
          if (!this.isCurrentSubmission(submission)) return;
          this.error = Boolean(error.cartError);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form', productVariantId: formData.get('id') || formData.get('items[0][id]'),
            errors: error.cartError?.errors || error.message, message: error.message,
          });
          this.handleErrorMessage(error.message || window.cartStrings?.error);
          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (this.error && soldOutMessage) {
            this.submitButton.classList.add('is-sold-out');
            this.submitButtonText?.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
          } else this.error = false;
        } finally {
          if (this.isCurrentSubmission(submission)) {
            submission.button.classList.remove('loading');
            if (!this.error) submission.button.removeAttribute('aria-disabled');
            submission.spinner?.classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          }
        }
      }

      getQueuedUpsellItems() {
        return Array.from(this.form.sugarUpsellComponents || []).flatMap((component) =>
          (component.queuedItems || []).map((item) => ({ id: parseInt(item.variantId, 10), quantity: 1 }))
            .filter((item) => Number.isFinite(item.id))
        );
      }

      clearQueuedUpsells(submission) {
        submission.upsells.forEach(({ component, items }) => {
          component.queuedItems = (component.queuedItems || []).filter((item) => !items.includes(item));
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      setSubmitButtonDefaultText(textHtml) {
        if (!this.submitButtonText || typeof textHtml !== 'string' || textHtml.trim() === '') return;
        this.submitButtonTextOriginalHtml = textHtml;
        if (!this.submitButton.hasAttribute('disabled')) {
          this.submitButtonText.innerHTML = textHtml;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          const isSoldOut = text === window.variantStrings?.soldOut;
          this.submitButton.classList.toggle('is-sold-out', isSoldOut);
          this.submitButtonText?.classList.remove('hidden');
          soldOutMessage?.classList.add('hidden');

          if (text && this.submitButtonText) {
            if (isSoldOut && window.variantStrings?.soldOutHtml) {
              this.submitButtonText.innerHTML = window.variantStrings.soldOutHtml;
            } else {
              this.submitButtonText.textContent = text;
            }
          }
        } else {
          this.submitButton.classList.remove('is-sold-out');
          this.submitButton.removeAttribute('disabled');
          if (!this.submitButton.classList.contains('loading')) {
            this.submitButton.removeAttribute('aria-disabled');
            this.error = false;
          }
          if (!this.submitButtonText) return;
          this.submitButtonText.classList.remove('hidden');
          soldOutMessage?.classList.add('hidden');
          if (this.submitButtonTextOriginalHtml) {
            this.submitButtonText.innerHTML = this.submitButtonTextOriginalHtml;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      async handleOffersSubmit(offer, quantity, variantId, isLocked) {
        if (!this.form?.hasAttribute('data-product-form-ready')) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const offersBlock = offer.closest('.offers');
        const alpineData = offersBlock?._x_dataStack?.[0];
        // Runtime/Alpine owns initialization; an SSR selected class is not readiness.
        if (!alpineData || alpineData.destroyed || !alpineData.productVariants?.length || alpineData.selectedId !== offer.dataset.blockId) {
          const startupStatus = this.querySelector('[data-product-form-startup-status]');
          if (startupStatus) startupStatus.hidden = false;
          return;
        }
        const submission = this.captureSubmission();
        this.handleErrorMessage();

        // Check for selling_plan from subscriptions block
        const sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
        const sellingPlanId = (sellingPlanInput && !sellingPlanInput.disabled && sellingPlanInput.value)
          ? parseInt(sellingPlanInput.value, 10)
          : null;

        // Build cart items
        const items = [];
        if (isLocked) {
          // Locked variant: all items use the same variant
          for (let i = 0; i < quantity; i++) {
            const item = { id: parseInt(variantId, 10), quantity: 1 };
            if (sellingPlanId) item.selling_plan = sellingPlanId;
            items.push(item);
          }
        } else {
          // Check for per-slot variant selections from Alpine state
          const blockId = offer.dataset.blockId;

          for (let slot = 1; slot <= quantity; slot++) {
            let slotVariantId = alpineData?.selectedVariants?.[blockId]?.[slot];
            const container = offer.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);

            // Visible separated controls can change before Alpine binds events.
            if (offersBlock.dataset.enableVariants === 'true' && offersBlock.dataset.variantType === 'dropdown' && alpineData.combineOptions === false && alpineData.productVariants.length > 1) {
              const options = Array.from(container?.querySelectorAll('.offer__option-select') || [], (select) => select.value);
              const variant = alpineData.productVariants.find((candidate) =>
                candidate.options.length === options.length && candidate.options.every((value, index) => value === options[index]));
              if (!variant) {
                const startupStatus = this.querySelector('[data-product-form-startup-status]');
                if (startupStatus) startupStatus.hidden = false;
                return;
              }
              slotVariantId = variant.id;
            }

            // Fallback: read from DOM
            if (!slotVariantId) {
              const dropdown = container?.querySelector('.offer__variant-select');
              const pill = container?.querySelector('.offer__variant-pill.is-active');
              slotVariantId = dropdown?.value || pill?.dataset.variantId;
            }

            const item = { id: parseInt(slotVariantId || variantId, 10), quantity: 1 };
            if (sellingPlanId) item.selling_plan = sellingPlanId;
            items.push(item);
          }
        }

        // Personalization belongs to every purchased offer slot, never its gifts.
        const properties = {};
        for (const [key, value] of new FormData(this.form)) {
          const property = key.match(/^properties\[(.*)\]$/);
          if (property && (!(value instanceof File) || value.name)) properties[property[1]] = value;
        }
        if (Object.keys(properties).length) {
          items.forEach((item) => { item.properties = { ...properties }; });
        }

        // Add offer gifts/upsells (if configured on the selected offer card)
        if (offer) {
          const offerId = offer.dataset.blockId;
          const qualifyingProductId = offer.dataset.productId || '';
          let removedGifts = {};

          try {
            const storageKey = `sugar_offers_removed_gifts_${window.location.pathname}`;
            const stored = localStorage.getItem(storageKey);
            removedGifts = stored ? JSON.parse(stored) : {};
          } catch (e) {
            removedGifts = {};
          }

          const giftElements = offer.querySelectorAll('.offer__gift[data-variant-id]');
          giftElements.forEach((giftEl) => {
            const giftType = giftEl.dataset.giftType;
            const giftVariantIdRaw = giftEl.dataset.variantId;
            const giftVariantId = parseInt(giftVariantIdRaw, 10);
            if (!Number.isFinite(giftVariantId)) return;

            // Respect persisted "removed gift" preference from offers block.
            if (offerId && removedGifts[`${offerId}_${giftVariantIdRaw}`] === true) return;

            // Upsells only add when checked.
            if (giftType === 'upsell') {
              const checkbox = giftEl.querySelector('.offer__gift-checkbox');
              if (!checkbox?.classList.contains('is-checked')) return;
            }

            const giftItem = {
              id: giftVariantId,
              quantity: 1,
              properties: {
                _offer_gift: offerId || '',
                _offer_gift_type: giftType || 'gift',
                _gift_for_product: qualifyingProductId,
              },
            };

            items.push(giftItem);
          });
        }

        items.push(...this.getQueuedUpsellItems());

        submission.button.setAttribute('aria-disabled', true);
        submission.button.classList.add('loading');
        submission.spinner?.classList.remove('hidden');

        try {
          // Snapshot the intent before waiting for the shared drawer definition.
          const cart = await this.getUpgradedCart();
          if (!this.isCurrentSubmission(submission)) return;
          this.cart = cart;
          // Build sections parameter for cart add request
          const sections = this.cart && typeof this.cart.getSectionsToRender === 'function'
            ? this.cart.getSectionsToRender().map((section) => section.id)
            : [];

          const config = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
          };
          if (Object.values(properties).some((value) => value instanceof File)) {
            const body = new FormData();
            items.forEach((item, index) => {
              for (const [key, value] of Object.entries(item)) {
                if (key === 'properties') {
                  for (const [name, property] of Object.entries(value)) {
                    body.append(`items[${index}][properties][${name}]`, property);
                  }
                } else {
                  body.append(`items[${index}][${key}]`, value);
                }
              }
            });
            body.append('sections', sections);
            body.append('sections_url', window.location.pathname);
            delete config.headers['Content-Type'];
            config.body = body;
          }
          const result = await window.SugarTheme.cart.request('add', config.body instanceof FormData ? config.body : JSON.parse(config.body), {
            source: 'product-form', context: submission.form, open: !submission.skipCart,
          });

          if (!this.isCurrentSubmission(submission)) return result;
          this.clearQueuedUpsells(submission);

          // Skip cart: redirect directly to checkout
          if (submission.skipCart) {
            window.location.href = '/checkout';
            return result;
          }

          if (!this.cart) window.location.href = routes.cart_url;
          return result;
        } catch (err) {
          if (!this.isCurrentSubmission(submission)) return;
          this.handleErrorMessage(err.message || 'Unable to add items to cart.');
        } finally {
          if (this.isCurrentSubmission(submission)) {
            submission.button.classList.remove('loading');
            submission.button.removeAttribute('aria-disabled');
            submission.spinner?.classList.add('hidden');
          }
        }
      }
    }
  );
}
